import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { queryKeys } from "@/lib/queryKeys";
import { invalidateFinancesChantier } from "@/lib/queryInvalidation";
import { isValidUUID } from "@/types/schemas";
import { useCalculations } from "@/hooks/useCalculations";
import { useChantierMetrics } from "@/hooks/useChantierMetrics";
import { getRentabilityBadge } from "@/lib/rentabilityBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChantierFormDialog } from "@/components/chantiers/ChantierFormDialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import {
  ArrowLeft, Building, Users, FileText, Receipt,
  AlertTriangle, TrendingUp, Calendar, MapPin, Edit, Trash2, Brain, CheckCircle2, CreditCard, Loader2
} from "lucide-react";
import QuoteManager from "@/components/project/QuoteManager";
import PaiementClient from "@/components/project/PaiementClient";
import InvoiceManager from "@/components/project/InvoiceManager";
import AffectationsList from "@/components/project/AffectationsList";
import ExpensesManager from "@/components/project/ExpensesManager";
import ExportManager from "@/components/ExportManager";
import ChantierKpis from "@/components/ChantierKpis";
import { ChartsSection } from "@/components/charts";
import { AnalyseIATab } from "@/components/chantiers/AnalyseIATab";
import ChantierClosureDialog from "@/components/project/ChantierClosureDialog";
import { ModalSuspensionChantier } from "@/components/project/ModalSuspensionChantier";
import { ModalRepriseChantier } from "@/components/project/ModalRepriseChantier";
import { ModalAnnulationChantier } from "@/components/project/ModalAnnulationChantier";
import { StatusDropdown } from "@/components/project/StatusDropdown";
import { useSuspension } from "@/hooks/useSuspension";
import { useChantierStatus } from "@/hooks/useChantierStatus";
import { StatutChantier, getModalForTransition } from "@/constants/statuts";
import { ChantierIcon } from "@/components/chantiers/ChantierIcon";
import { useHighlightButton } from "@/hooks/useHighlightButton";
import { TipsModal } from "@/components/tips/TipsModal";
import { cn } from "@/lib/utils";
import { useAnalyseIA } from "@/hooks/useAnalyseIA";
import { TabBadge, TAB_TRIGGER_CLASSES } from "@/components/ui/TabBadge";

interface Chantier {
  id: string;
  nom_chantier: string;
  reference_chantier?: string;
  client: string;
  adresse: string;
  description: string;
  duree_estimee: number;
  duree_estimee_jours?: number;
  budget_ht?: number;
  statut: string;
  date_creation: string;
  date_debut_prevue: string | null;
  date_debut_reelle?: string | null;
  date_fin_estimee?: string | null;
  date_fin_reelle?: string | null;
  etat_chantier?: string;
  pictogramme?: string;
  couleur?: string;
}

const ProjectDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { toast } = useToast();

  // État pour le TipsModal devis
  const [showDevisTips, setShowDevisTips] = useState(false);

  // État pour le TipsModal factures
  const [showFacturesTips, setShowFacturesTips] = useState(false);

  // État pour le TipsModal paiement
  const [showPaiementTips, setShowPaiementTips] = useState(false);

  // État pour le TipsModal budget ventilé
  const [showBudgetTips, setShowBudgetTips] = useState(false);
  const [autoOpenBudgetModal, setAutoOpenBudgetModal] = useState(false);
  // État pour le clignotement du bouton "Répartir mon budget"
  const [highlightBudgetButton, setHighlightBudgetButton] = useState(false);
  // État pour forcer l'onglet 'budget' dans ChartsSection (depuis alerte budget)
  const [chartsSectionDefaultTab, setChartsSectionDefaultTab] = useState<'budget' | 'rentabilite' | 'couts' | 'evolution' | undefined>(undefined);
  // Ref pour scroller vers la section Charts
  const chartsSectionRef = useRef<HTMLDivElement>(null);

  // Détection de l'arrivée depuis la checklist (via URL param)
  const highlightParam = searchParams.get('highlight');
  const isFromChecklist = !!highlightParam;

  // Highlights depuis la checklist (20 secondes) - guide l'utilisateur vers le bon onglet
  const isAddDevisHighlighted = useHighlightButton('add-devis');
  const isAddAffectationHighlighted = useHighlightButton('add-affectation');
  const isAddFactureHighlighted = useHighlightButton('add-facture');
  const isAddFraisHighlighted = useHighlightButton('add-frais');
  const isAddPaiementHighlighted = useHighlightButton('add-paiement');
  const isLaunchAnalysisHighlighted = useHighlightButton('launch-analysis');

  // Hook temps réel pour les métriques
  const { metrics, loading: metricsLoading, refresh: refreshMetrics } = useChantierMetrics(id || '');

  // Pourcentage encaissé pour le badge de l'onglet Paiement
  const { entrepriseId: subEntrepriseId } = useSubscription();
  const queryClient = useQueryClient();
  const { data: paiementTotals } = useQuery({
    queryKey: queryKeys.finances.paiementTotals(subEntrepriseId!, id!),
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_paiement_totals', { p_chantier_id: id! });
      if (error) throw error;
      return data?.[0] ?? null;
    },
    enabled: !!subEntrepriseId && !!id,
    staleTime: 2 * 60 * 1000,
  });

  // Hook centralisé pour la gestion des statuts
  const { changeStatus } = useChantierStatus();
  
  const [chantier, setChantier] = useState<Chantier | null>(null);
  const [devis, setDevis] = useState<any[]>([]);
  const [factures, setFactures] = useState<any[]>([]);
  const [membres, setMembres] = useState<any[]>([]);
  const [affectations, setAffectations] = useState<any[]>([]);
  const [frais, setFrais] = useState<any[]>([]);

  // Fix B2 : Références stables pour les props des modaux
  // Empêche les useEffect des modaux de fire quand les données n'ont pas réellement changé
  // (loadProjectData crée de nouvelles références même si les valeurs sont identiques)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableChantierData = useMemo(() => chantier, [chantier?.id, chantier?.etat_chantier, chantier?.budget_ht]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableMetrics = useMemo(() => metrics, [JSON.stringify(metrics)]);

  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeletingChantier, setIsDeletingChantier] = useState(false);
  const [closureDialogOpen, setClosureDialogOpen] = useState(false);
  // États pour les modals de transition de statut
  const [suspensionModalOpen, setSuspensionModalOpen] = useState(false);
  const [repriseModalOpen, setRepriseModalOpen] = useState(false);
  const [annulationModalOpen, setAnnulationModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("quote");

  // Hook pour vérifier si l'utilisateur peut lancer une analyse IA (cooldown passé)
  const { limites: limitesIA, loadingLimites: loadingLimitesIA } = useAnalyseIA(id || '', entrepriseId || '');

  // État pour tracker si l'utilisateur a cliqué sur l'onglet factures (persist en localStorage par chantier)
  const [hasSeenInvoicesTab, setHasSeenInvoicesTab] = useState(() => {
    if (!id) return false;
    return localStorage.getItem(`b8ild_invoices_tab_seen_${id}`) === 'true';
  });

  // Calculs automatiques (toujours en HT pour la rentabilité)
  const totalFactures = factures.reduce((sum, f) => sum + Number(f.montant_ht), 0);
  const totalFrais = frais.reduce((sum, f) => sum + Number(f.montant_ht ?? f.montant_total), 0);
  const coutsFixes = totalFactures + totalFrais;

  // Trouver le devis actif
  const devisActif = devis.find(d => d.actif);

  // === FLOW GUIDÉ SÉQUENTIEL ===
  // L'utilisateur doit compléter les étapes dans l'ordre: Devis → Équipe → Factures → Frais → Paiement → Analyse IA
  // Chaque onglet/bouton clignote jusqu'à ce que l'étape soit complétée
  const hasDevis = devis.length > 0;
  const hasAffectations = affectations.length > 0;
  const hasFactures = factures.length > 0;
  const hasFrais = frais.length > 0;
  // Le paiement est configuré si le statut n'est pas 'non_facture' OU si des paiements ont déjà été encaissés
  const hasPaiementConfigured = (chantier?.statut_paiement && chantier.statut_paiement !== 'non_facture') || (paiementTotals?.pourcentage_encaisse != null && paiementTotals.pourcentage_encaisse > 0);

  // === DONNÉES POUR LES BADGES D'ONGLETS ===
  const devisCount = devis.length;
  const devisStatusDot: 'emerald' | 'orange' | undefined = devis.some(d => d.statut === 'accepte')
    ? 'emerald'
    : devisCount > 0 ? 'orange' : undefined;
  const affectationsCount = affectations.length;
  const teamStatusDot: 'emerald' | undefined = affectationsCount > 0 ? 'emerald' : undefined;
  const facturesCount = factures.length;
  const fraisCount = frais.length;
  // Paiement: 0% = dot rouge, 1-99% = label "X%", 100% = checkmark vert
  const paiementPct = paiementTotals?.pourcentage_encaisse ?? null;
  const paiementCheckmark = paiementPct === 100;
  const paiementLabel = paiementPct != null && paiementPct > 0 && paiementPct < 100
    ? `${Math.round(paiementPct)}%`
    : undefined;
  const paiementDot: 'red' | undefined = paiementPct === 0 ? 'red' : undefined;

  // Le flow guidé est actif seulement si on arrive depuis la checklist
  const isGuidedFlowActive = isAddDevisHighlighted || isAddAffectationHighlighted || isAddFactureHighlighted || isAddFraisHighlighted || isAddPaiementHighlighted || isLaunchAnalysisHighlighted;

  // Déterminer quel onglet doit clignoter (basé sur le flow séquentiel)
  // On clignote seulement si le flow guidé est actif ET l'étape n'est pas complétée ET l'utilisateur n'est pas sur cet onglet
  const shouldHighlightDevisTab = isGuidedFlowActive && !hasDevis && !loading && activeTab !== 'quote';
  const shouldHighlightTeamTab = isGuidedFlowActive && hasDevis && !hasAffectations && !loading && activeTab !== 'team';
  // L'onglet Factures clignote de manière permanente si:
  // - Il y a au moins une affectation (équipe/membre affecté)
  // - L'utilisateur n'a pas encore cliqué sur l'onglet factures
  // - Il n'y a pas encore de factures
  // - L'utilisateur n'est pas déjà sur l'onglet factures
  const shouldHighlightInvoicesTab = hasAffectations && !hasSeenInvoicesTab && !hasFactures && !loading && activeTab !== 'invoices';
  const shouldHighlightExpensesTab = isGuidedFlowActive && hasDevis && hasAffectations && hasFactures && !hasFrais && !loading && activeTab !== 'expenses';
  // Paiement clignote si:
  // - on vient de la checklist avec highlight=add-paiement ET le paiement n'est pas encore configuré
  // - OU on est dans le flow guidé et on a complété les frais ET le paiement n'est pas encore configuré
  const shouldHighlightPaiementTab = !loading && activeTab !== 'paiement' && !hasPaiementConfigured && (
    isAddPaiementHighlighted ||
    (isGuidedFlowActive && hasDevis && hasAffectations && hasFactures && hasFrais && !isAddPaiementHighlighted)
  );
  // Analyse IA clignote si:
  // - on vient de la checklist avec highlight=launch-analysis
  // - OU on est dans le flow guidé et le paiement est configuré (mais pas si on vient directement de la checklist analyse)
  const shouldHighlightAnalysisTab = !loading && activeTab !== 'ai-analysis' && (
    isLaunchAnalysisHighlighted ||
    (isGuidedFlowActive && hasDevis && hasAffectations && hasFactures && hasFrais && hasPaiementConfigured && !isLaunchAnalysisHighlighted)
  );

  // Déterminer quel bouton d'action doit clignoter (dans le composant enfant)
  const shouldHighlightAddDevisButton = isGuidedFlowActive && !hasDevis && !loading;
  const shouldHighlightAddAffectationButton = isGuidedFlowActive && hasDevis && !hasAffectations && !loading;
  const shouldHighlightAddFactureButton = isGuidedFlowActive && hasDevis && hasAffectations && !hasFactures && !loading;
  const shouldHighlightAddFraisButton = isGuidedFlowActive && hasDevis && hasAffectations && hasFactures && !hasFrais && !loading;

  const calculations = useCalculations({
    membres,
    budget_devis: devisActif?.montant_ht || 0, // Utiliser HT pour les calculs de rentabilité
    couts_fixes: coutsFixes,
    date_debut: chantier?.date_debut_prevue,
  });

  // Hook pour les suspensions (récupérer l'info de suspension pour le modal de reprise)
  const {
    suspensionActive: suspensionInfo,
    loadSuspensionActive,
    loading: suspensionLoading
  } = useSuspension({
    chantierId: id || ''
  });

  // Charger l'info de suspension si le chantier est suspendu
  useEffect(() => {
    if (chantier?.etat_chantier === 'suspendu' && id) {
      loadSuspensionActive();
    }
  }, [chantier?.etat_chantier, id, loadSuspensionActive]);

  // Gestion des transitions de statut via le StatusDropdown
  const handleStatusTransition = async (nouveauStatut: StatutChantier, modal?: string) => {
    // Si un modal est requis, l'ouvrir
    if (modal) {
      switch (modal) {
        case 'ModalSuspensionChantier':
          setSuspensionModalOpen(true);
          return;
        case 'ModalRepriseChantier':
          setRepriseModalOpen(true);
          return;
        case 'ChantierClosureDialog':
          setClosureDialogOpen(true);
          return;
        case 'ModalAnnulationChantier':
          setAnnulationModalOpen(true);
          return;
        case 'ModalReouvertureChantier':
          // Pour la réouverture, on demande confirmation puis on met à jour
          if (confirm('Voulez-vous rouvrir ce chantier pour des travaux supplémentaires ?')) {
            await updateChantierStatus('en_cours');
          }
          return;
        case 'ModalReactivationChantier':
          // Pour la réactivation depuis annulé
          if (confirm('Voulez-vous réactiver ce chantier ?')) {
            await updateChantierStatus(nouveauStatut);
          }
          return;
        default:
          if (import.meta.env.DEV) console.warn('Modal non reconnu:', modal);
      }
    }

    // Sinon, mise à jour directe du statut
    await updateChantierStatus(nouveauStatut);
  };

  // Fonction helper pour mettre à jour le statut
  // Utilise le hook centralisé useChantierStatus pour validation et synchronisation
  const updateChantierStatus = async (nouveauStatut: StatutChantier) => {
    if (!id || !chantier) return;

    const currentStatus = (chantier.etat_chantier || 'en_cours') as StatutChantier;

    // Utiliser le hook centralisé
    const result = await changeStatus(id, currentStatus, nouveauStatut);

    if (result.success) {
      // Le hook émet les événements de synchronisation
      // On recharge les données localement aussi
      handleDataUpdate();
    }
    // Si modal requis ou erreur, le hook a déjà géré l'affichage
  };

  useEffect(() => {
    if (id && user) {
      loadProjectData();
    }
  }, [id, user]);

  // Ouvrir automatiquement la modal de clôture si ?terminer=true ou ?action=closure dans l'URL
  useEffect(() => {
    const shouldOpenClosure = searchParams.get('terminer') === 'true' || searchParams.get('action') === 'closure';
    if (shouldOpenClosure && chantier && !loading) {
      setClosureDialogOpen(true);
      // Supprimer le paramètre de l'URL pour éviter de rouvrir la modal au refresh
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, chantier, loading]);

  // Gérer le paramètre tab dans l'URL (ex: /projects/123?tab=ai-analysis)
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam) {
      // Mapping des paramètres URL vers les valeurs de tabs
      const tabMapping: Record<string, string> = {
        'ai-analysis': 'ai-analysis',
        'quote': 'quote',
        'team': 'team',
        'invoices': 'invoices',
        'expenses': 'expenses',
        'paiement': 'paiement',
      };
      const targetTab = tabMapping[tabParam];
      if (targetTab) {
        setActiveTab(targetTab);
        // Nettoyer l'URL après avoir changé d'onglet
        setSearchParams({}, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  // Gérer le paramètre showBudgetTip dans l'URL (depuis la checklist budget ventilé)
  useEffect(() => {
    const showBudgetTip = searchParams.get('showBudgetTip') === 'true';
    if (showBudgetTip && !loading) {
      // Afficher UNIQUEMENT le TipsModal (pas le modal de ventilation)
      // Le scroll + onglet Budget + clignotement se feront à la fermeture du tips
      setShowBudgetTips(true);
      // Nettoyer l'URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, loading]);

  // Gérer le paramètre scrollTo=budget-details (depuis alertes budget PilotagePanel)
  useEffect(() => {
    const scrollTo = searchParams.get('scrollTo');
    if (scrollTo === 'budget-details' && !loading) {
      // Forcer l'onglet 'budget' dans ChartsSection
      setChartsSectionDefaultTab('budget');

      // Scroller vers la section ChartsSection après un petit délai (laisser le temps au rendu)
      setTimeout(() => {
        chartsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);

      // Nettoyer l'URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, loading]);

  // Changer automatiquement l'onglet actif quand on arrive depuis la checklist
  useEffect(() => {
    if (isAddDevisHighlighted) setActiveTab('quote');
    else if (isAddAffectationHighlighted) setActiveTab('team');
    else if (isAddFactureHighlighted) {
      setActiveTab('invoices');
      // Marquer l'onglet factures comme vu quand on arrive depuis la checklist
      if (id) {
        setHasSeenInvoicesTab(true);
        localStorage.setItem(`b8ild_invoices_tab_seen_${id}`, 'true');
      }
    }
    else if (isAddFraisHighlighted) setActiveTab('expenses');
    else if (isAddPaiementHighlighted) setActiveTab('paiement');
    else if (isLaunchAnalysisHighlighted) setActiveTab('ai-analysis');
  }, [isAddDevisHighlighted, isAddAffectationHighlighted, isAddFactureHighlighted, isAddFraisHighlighted, isAddPaiementHighlighted, isLaunchAnalysisHighlighted, id]);

  // Afficher le TipsModal devis quand on arrive depuis la checklist sur l'onglet devis
  useEffect(() => {
    const fromChecklist = (location.state as any)?.fromChecklist;
    const showTips = (location.state as any)?.showDevisTips;
    const tipsSeen = localStorage.getItem('b8ild_tips_devis_seen');

    // Afficher si on vient de la checklist vers devis OU si showDevisTips ET pas encore vu
    if ((fromChecklist && isAddDevisHighlighted) || showTips) {
      if (!tipsSeen) {
        setShowDevisTips(true);
      }
    }
  }, [location.state, isAddDevisHighlighted]);

  // Fermeture du TipsModal devis
  const handleCloseDevisTips = () => {
    setShowDevisTips(false);
    localStorage.setItem('b8ild_tips_devis_seen', 'true');
  };

  // Afficher le TipsModal factures quand on arrive depuis la checklist
  useEffect(() => {
    const fromChecklist = (location.state as any)?.fromChecklist;
    const tipsSeen = localStorage.getItem('b8ild_tips_factures_seen');
    if (fromChecklist && isAddFactureHighlighted && !tipsSeen) {
      setShowFacturesTips(true);
    }
  }, [location.state, isAddFactureHighlighted]);

  // Fermeture du TipsModal factures
  const handleCloseFacturesTips = () => {
    setShowFacturesTips(false);
    localStorage.setItem('b8ild_tips_factures_seen', 'true');
  };

  // Afficher le TipsModal paiement quand l'utilisateur va sur l'onglet paiement pour la première fois
  useEffect(() => {
    if (activeTab === 'paiement') {
      const tipsSeen = localStorage.getItem('b8ild_tips_paiement_seen');
      if (!tipsSeen) {
        setShowPaiementTips(true);
      }
    }
  }, [activeTab]);

  // Fermeture du TipsModal paiement
  const handleClosePaiementTips = () => {
    setShowPaiementTips(false);
    localStorage.setItem('b8ild_tips_paiement_seen', 'true');
  };

  // Fermeture du TipsModal budget ventilé
  const handleCloseBudgetTips = () => {
    setShowBudgetTips(false);
    localStorage.setItem('b8ild_tips_budget_seen', 'true');

    // Activer le clignotement du bouton "Répartir mon budget"
    // (ChartsSection détecte ce flag et passe automatiquement sur l'onglet Budget)
    setHighlightBudgetButton(true);

    // Scroller vers la section ChartsSection après un petit délai (laisser le temps au rendu)
    setTimeout(() => {
      chartsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Fix B2 : Debounce pour coalescer les events Realtime en rafale
  // Quand l'onglet redevient actif, Supabase reconnecte et rejoue tous les events
  // accumulés → sans debounce, loadProjectData(true) tirait 4 fois → nouvelles refs
  // → useEffect des modaux reset le state local → saisie perdue
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const debouncedRealtimeRefresh = useCallback(() => {
    if (realtimeDebounceRef.current) {
      clearTimeout(realtimeDebounceRef.current);
    }
    realtimeDebounceRef.current = setTimeout(() => {
      if (import.meta.env.DEV) console.log('[ProjectDetail] Realtime refresh (debouncé)');
      loadProjectData(true);
      refreshMetrics();
      if (subEntrepriseId && id) invalidateFinancesChantier(queryClient, subEntrepriseId, id);
    }, 500);
  }, [id, refreshMetrics, queryClient, subEntrepriseId]);

  // Cleanup du debounce
  useEffect(() => {
    return () => {
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
    };
  }, []);

  // Subscription Realtime pour toutes les données du projet
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel(`project_detail:${id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'devis',
          filter: `chantier_id=eq.${id}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'affectations_chantiers',
          filter: `chantier_id=eq.${id}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'factures_fournisseurs',
          filter: `chantier_id=eq.${id}`,
        },
        debouncedRealtimeRefresh
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'frais_chantier',
          filter: `chantier_id=eq.${id}`,
        },
        debouncedRealtimeRefresh
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, debouncedRealtimeRefresh]);

  // Fonction combinée : recharge les données ET rafraîchit les métriques + caches React Query
  const handleDataUpdate = async () => {
    await loadProjectData();
    refreshMetrics();
    if (subEntrepriseId && id) invalidateFinancesChantier(queryClient, subEntrepriseId, id);
  };

  // silentRefresh = true → ne pas afficher de loader (pour refresh en arrière-plan via Realtime)
  const loadProjectData = async (silentRefresh = false) => {
    if (!id || !user) return;

    // Ne pas afficher le loader si c'est un refresh silencieux
    if (!silentRefresh) {
      setLoading(true);
    }
    try {
      // Load entreprise ID
      const { data: entrepriseData } = await supabase
        .from("entreprises")
        .select("id")
        .eq("proprietaire_user_id", user.id)
        .single();
      
      if (entrepriseData) {
        setEntrepriseId(entrepriseData.id);
      }

      // Charger le chantier avec verification d'ownership via entreprise_id
      // Cela empeche un utilisateur d'acceder aux chantiers d'autres entreprises
      const { data: chantierData, error: chantierError } = await supabase
        .from("chantiers")
        .select("*")
        .eq("id", id)
        .eq("entreprise_id", entrepriseData?.id) // Verification ownership
        .single();

      if (chantierError || !chantierData) {
        console.error("[ProjectDetail] Chantier non trouve ou acces refuse:", chantierError);
        toast({
          title: "Acces refuse",
          description: "Ce chantier n'existe pas ou ne vous appartient pas",
          variant: "destructive",
        });
        navigate("/projects");
        return;
      }
      setChantier(chantierData);

      // Charger tous les devis (triés par version)
      const { data: devisData } = await supabase
        .from("devis")
        .select("*")
        .eq("chantier_id", id)
        .order('created_at', { ascending: false });
      setDevis(devisData || []);

      // Charger les factures
      const { data: facturesData } = await supabase
        .from("factures_fournisseurs")
        .select("*")
        .eq("chantier_id", id);
      setFactures(facturesData || []);

      // Charger les membres affectés avec leurs jours (équipe de base)
      const { data: equipeData } = await supabase
        .from("equipe_chantier")
        .select(`
          id,
          jours_travailles,
          membre:membres_equipe(
            id,
            prenom,
            nom,
            taux_horaire,
            charges_salariales,
            charges_patronales,
            coefficient_charges,
            heures_par_jour,
            poste,
            equipe_id
          )
        `)
        .eq("chantier_id", id);

      const membresEquipeBase = equipeData
        ?.filter(e => e.membre !== null)
        .map(e => ({
          ...e.membre,
          jours_travailles: e.jours_travailles || 0,
          isFromDelay: false, // Marqueur pour différencier
        })) || [];

      // Charger les membres des affectations de retard (affectations_chantiers avec note "Retard chantier")
      const { data: affectationsRetardData } = await supabase
        .from("affectations_chantiers")
        .select(`
          id,
          membre_equipe_id,
          jours_travailles,
          date_debut,
          date_fin,
          notes,
          membre:membres_equipe(
            id,
            prenom,
            nom,
            taux_horaire,
            charges_salariales,
            charges_patronales,
            coefficient_charges,
            heures_par_jour,
            poste,
            equipe_id
          )
        `)
        .eq("chantier_id", id)
        .like("notes", "Retard chantier%");

      // Extraire les membres des affectations retard
      // Note: a.membre peut être un objet ou un tableau selon la config Supabase
      const membresRetard = affectationsRetardData
        ?.filter(a => a.membre !== null)
        .map(a => {
          // S'assurer qu'on a un objet membre valide (pas un tableau)
          const membreData = Array.isArray(a.membre) ? a.membre[0] : a.membre;
          return {
            ...membreData,
            jours_travailles: a.jours_travailles || 0,
            isFromDelay: true, // Marqueur pour identifier les membres du retard
            affectation: {
              date_debut: a.date_debut,
              date_fin: a.date_fin,
            },
          };
        })
        .filter(m => m && m.id) || []; // Filtrer les entrées invalides

      // Fusionner les deux listes :
      // - Pour les membres de l'équipe de base qui ont aussi une affectation retard,
      //   on leur ajoute les infos de retard (jours supplémentaires, badge, période)
      // - Pour les membres uniquement dans les affectations retard, on les ajoute séparément

      // Créer un map des membres retard par leur ID
      const membresRetardMap = new Map<string, typeof membresRetard[0]>();
      membresRetard.forEach(m => {
        if (m.id) membresRetardMap.set(m.id, m);
      });

      const membresEquipeAvecRetard = membresEquipeBase.map(membre => {
        const membreRetard = membresRetardMap.get(membre.id);
        if (membreRetard) {
          // Ce membre est dans l'équipe de base ET a une affectation retard
          // On fusionne les données : jours de base + jours de retard
          return {
            ...membre,
            // NE PAS additionner les jours ici - garder jours_travailles de l'équipe base
            // Les jours de retard sont stockés séparément
            joursRetard: membreRetard.jours_travailles, // Jours supplémentaires de retard
            hasDelayAffectation: true, // Marqueur pour afficher le badge Retard
            affectationRetard: membreRetard.affectation, // Période de retard
          };
        }
        return membre;
      });

      // Membres uniquement dans les affectations retard (pas dans l'équipe de base)
      const membresIds = new Set(membresEquipeBase.map(m => m.id));
      const membresRetardUniques = membresRetard.filter(m => m.id && !membresIds.has(m.id));

      const membresAffectes = [...membresEquipeAvecRetard, ...membresRetardUniques];
      setMembres(membresAffectes);

      // Charger les frais (exclure les coûts de retard main d'œuvre car déjà comptés via affectations)
      const { data: fraisData } = await supabase
        .from("frais_chantier")
        .select("*")
        .eq("chantier_id", id);
      // Filtrer côté client pour exclure les frais de type retard (main d'œuvre ET pénalités)
      // car ils sont déjà comptés et affichés séparément dans le simulateur/bilan de retard
      const fraisFiltres = (fraisData || []).filter(
        (f) => {
          const typeLower = f.type_frais?.toLowerCase() || '';
          return !typeLower.includes('coût retard') && !typeLower.includes('pénalité de retard');
        }
      );
      setFrais(fraisFiltres);

      // Charger les affectations (pour le flow guidé)
      const { data: affectationsData } = await supabase
        .from("affectations_chantiers")
        .select("*")
        .eq("chantier_id", id);
      setAffectations(affectationsData || []);

    } catch (error: any) {
      console.error("Erreur chargement projet:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données du chantier",
        variant: "destructive",
      });
    } finally {
      // Ne pas mettre loading = false si c'était un refresh silencieux
      if (!silentRefresh) {
        setLoading(false);
      }
    }
  };

  const getStatusBadge = () => {
    const { rentabilite_pct } = calculations;
    const rentabilityBadge = getRentabilityBadge(rentabilite_pct || 0);
    
    return (
      <Badge 
        className={`font-bold px-3 py-1 border-2 ${rentabilityBadge.bgColor} ${rentabilityBadge.color}`}
      >
        {rentabilityBadge.emoji} {rentabilityBadge.label}
      </Badge>
    );
  };

  const handleDeleteChantier = async () => {
    setIsDeletingChantier(true);
    try {
      const { error } = await supabase
        .from("chantiers")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Succès",
        description: "Chantier supprimé avec succès",
      });

      navigate("/projects");
    } catch (error: any) {
      toast({
        title: "Erreur",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeletingChantier(false);
    }
  };

  const openEditDialog = () => {
    if (chantier) {
      setEditDialogOpen(true);
    }
  };

  if (loading) {
    return (
      <Card className="card-premium">
        <CardContent className="pt-16 pb-16 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
          <p className="text-muted-foreground text-lg font-medium">Chargement du chantier...</p>
        </CardContent>
      </Card>
    );
  }

  if (!chantier) {
    return (
      <Card className="card-premium">
        <CardContent className="pt-16 pb-16 text-center">
          <Building className="h-20 w-20 mx-auto text-muted-foreground mb-4" />
          <p className="text-xl font-semibold text-muted-foreground mb-4">Chantier introuvable</p>
          <Button onClick={() => navigate("/projects")} variant="primary" size="lg" className="hover-lift">
            <ArrowLeft className="mr-2 h-5 w-5" />
            Retour aux chantiers
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Conditions d'affichage des badges d'info
  const showConfigWarning = (!chantier.budget_ht || !chantier.duree_estimee_jours) &&
    !(devisActif && chantier.date_debut_prevue && chantier.date_fin_estimee);
  const showIABadge = !loadingLimitesIA && limitesIA?.peutAnalyser;

  return (
    <div className="space-y-8 animate-fade-up">
      {/* Header */}
      <div>
        <Button onClick={() => navigate("/projects")} variant="ghost" className="mb-6 hover-lift">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour aux chantiers
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-2">
            <h1 className="text-4xl font-black text-gradient-primary flex items-center gap-3">
              {chantier.pictogramme && chantier.couleur ? (
                <ChantierIcon
                  pictogramme={chantier.pictogramme}
                  couleur={chantier.couleur}
                  size="lg"
                  showBackground={true}
                />
              ) : (
                <Building className="h-9 w-9 text-primary" />
              )}
              {chantier.nom_chantier}
              {/* Badge Retard si des coûts de retard sont enregistrés */}
              {metrics && metrics.cout_retard > 0 && (
                <Badge className="ml-2 bg-orange-500/20 text-orange-500 border-orange-500/30 text-sm font-semibold">
                  Retard
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground text-lg flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {chantier.client} - {chantier.adresse}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Dropdown de statut intelligent */}
            <StatusDropdown
              statutActuel={(chantier.etat_chantier as StatutChantier) || 'brouillon'}
              dateDebutPrevue={chantier.date_debut_prevue}
              dateDebutReelle={chantier.date_debut_reelle}
              dateFinPrevue={chantier.date_fin_estimee}
              dateFinReelle={chantier.date_fin_reelle}
              onTransition={handleStatusTransition}
            />
            {/* Badge de rentabilité */}
            {getStatusBadge()}
            <Button variant="outline" onClick={openEditDialog}>
              <Edit className="h-4 w-4 mr-2" />
              Modifier
            </Button>
            <Button variant="destructive" onClick={() => setDeleteDialogOpen(true)}>
              <Trash2 className="h-4 w-4 mr-2" />
              Supprimer
            </Button>
            <ExportManager
              chantierId={id!}
              chantierData={chantier}
              membres={membres}
              devis={devisActif}
              allDevis={devis}
              factures={factures}
              frais={frais}
              calculations={calculations}
              metrics={metrics}
            />
          </div>
        </div>
      </div>

      {/* Badges d'info compacts */}
      {(showConfigWarning || showIABadge) && (
        <div className="flex items-center gap-2 flex-wrap">
          {showConfigWarning && (
            <button
              onClick={openEditDialog}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-warning/10 text-warning border border-warning/20 hover:bg-warning/20 transition-colors cursor-pointer"
            >
              <AlertTriangle className="h-3 w-3" />
              Configuration incomplète
              <span className="text-warning/60">·</span>
              <span className="underline underline-offset-2">Configurer</span>
            </button>
          )}
          {showIABadge && (
            <button
              onClick={() => setActiveTab('ai-analysis')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20 transition-colors cursor-pointer"
            >
              <Brain className="h-3 w-3" />
              Analyse IA disponible
            </button>
          )}
        </div>
      )}

      {/* Tabs de gestion */}
      <Card className="card-premium">
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          // Marquer l'onglet factures comme vu quand l'utilisateur clique dessus
          if (tab === 'invoices' && !hasSeenInvoicesTab && id) {
            setHasSeenInvoicesTab(true);
            localStorage.setItem(`b8ild_invoices_tab_seen_${id}`, 'true');
          }
        }} className="w-full">
          <TabsList className="grid w-full grid-cols-6 h-14 bg-muted/50 p-1 border-b border-white/10 [&>button>svg]:hidden [&>button>svg]:xl:block">
            <TabsTrigger
              value="quote"
              className={cn(
                TAB_TRIGGER_CLASSES,
                shouldHighlightDevisTab && "animate-highlight-pulse"
              )}
            >
              <FileText className="h-5 w-5 mr-2" />
              Devis
              <TabBadge count={devisCount} dotColor={devisStatusDot} />
            </TabsTrigger>
            <TabsTrigger
              value="team"
              className={cn(
                TAB_TRIGGER_CLASSES,
                shouldHighlightTeamTab && "animate-highlight-pulse"
              )}
            >
              <Users className="h-5 w-5 mr-2" />
              Équipe
              <TabBadge count={affectationsCount} dotColor={teamStatusDot} />
            </TabsTrigger>
            <TabsTrigger
              value="invoices"
              className={cn(
                TAB_TRIGGER_CLASSES,
                shouldHighlightInvoicesTab && "animate-highlight-pulse"
              )}
            >
              <Receipt className="h-5 w-5 mr-2" />
              Factures
              <TabBadge count={facturesCount} />
            </TabsTrigger>
            <TabsTrigger
              value="expenses"
              className={cn(
                TAB_TRIGGER_CLASSES,
                shouldHighlightExpensesTab && "animate-highlight-pulse"
              )}
            >
              <Receipt className="h-5 w-5 mr-2" />
              Coûts annexes
              <TabBadge count={fraisCount} />
            </TabsTrigger>
            <TabsTrigger
              value="paiement"
              className={cn(
                TAB_TRIGGER_CLASSES,
                shouldHighlightPaiementTab && "animate-highlight-pulse"
              )}
            >
              <CreditCard className="h-5 w-5 mr-2" />
              Paiement
              <TabBadge checkmark={paiementCheckmark} label={paiementLabel} labelBgClass="bg-orange-500/20" labelTextClass="text-orange-400" dotColor={paiementDot} />
            </TabsTrigger>
            <TabsTrigger
              value="ai-analysis"
              className={cn(
                TAB_TRIGGER_CLASSES,
                shouldHighlightAnalysisTab && "animate-highlight-pulse"
              )}
            >
              <Brain className="h-5 w-5 mr-2" />
              Analyse IA
              <TabBadge dotColor={showIABadge ? 'purple' : undefined} pulse={!!showIABadge} />
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quote" className="mt-6">
            <QuoteManager chantierId={id!} devis={devis} onUpdate={handleDataUpdate} isHighlighted={shouldHighlightAddDevisButton} />
          </TabsContent>

          <TabsContent value="invoices" className="mt-6">
            <InvoiceManager chantierId={id!} factures={factures} onUpdate={handleDataUpdate} isHighlighted={shouldHighlightAddFactureButton} />
          </TabsContent>

          <TabsContent value="team" className="mt-6">
            {entrepriseId && (
              <AffectationsList
                chantierId={id!}
                entrepriseId={entrepriseId}
                isHighlighted={shouldHighlightAddAffectationButton}
              />
            )}
          </TabsContent>

          <TabsContent value="expenses" className="mt-6">
            <ExpensesManager chantierId={id!} frais={frais} onUpdate={handleDataUpdate} isHighlighted={shouldHighlightAddFraisButton} />
          </TabsContent>

          <TabsContent value="paiement" className="mt-6">
            <PaiementClient
              chantierId={id!}
              montantDevisHT={devisActif?.montant_ht || 0}
              montantDevisTTC={devisActif?.montant_ttc || 0}
              onUpdate={handleDataUpdate}
              isGuidedFlow={isGuidedFlowActive}
            />
          </TabsContent>

          <TabsContent value="ai-analysis" className="mt-6">
            {entrepriseId && chantier && (
              <AnalyseIATab
                chantierId={id!}
                entrepriseId={entrepriseId}
                chantierData={{
                  nom: chantier.nom_chantier,
                  client: chantier.client,
                  adresse: chantier.adresse,
                  pictogramme: chantier.pictogramme || 'Home',
                  statut: chantier.etat_chantier || chantier.statut,
                  date_debut: chantier.date_debut_prevue || chantier.date_creation,
                  date_fin_prevue: chantier.date_fin_estimee || '',
                  date_fin_reelle: chantier.date_fin_reelle || null,
                  montant_devis_ht: devisActif?.montant_ht || 0,
                  montant_devis_ttc: devisActif?.montant_ttc || 0,
                  // Budgets prévisionnels (depuis le devis ou estimations)
                  budget_materiaux: devisActif?.budget_materiaux || 0,
                  budget_main_oeuvre: devisActif?.budget_main_oeuvre || 0,
                  budget_frais: devisActif?.budget_frais || 0,
                  // Coûts réels engagés
                  reel_materiaux: totalFactures, // Factures fournisseurs (matériaux)
                  reel_main_oeuvre: calculations.cout_total_equipe || 0, // Coût équipe calculé
                  reel_frais: totalFrais, // Frais annexes
                  region: null,
                  // Données équipe avec détails
                  equipes: membres.map(m => ({
                    nom: `${m.prenom || ''} ${m.nom || ''}`.trim(),
                    poste: m.poste || 'Ouvrier',
                    taux_horaire: m.taux_horaire || 0,
                    jours_travailles: m.jours_travailles || 0,
                    heures_total: (m.jours_travailles || 0) * (m.heures_par_jour || 8),
                    cout_total: (m.jours_travailles || 0) * (m.heures_par_jour || 8) * (m.taux_horaire || 0),
                  })),
                  // Totaux équipe calculés
                  jours_totaux_equipe: calculations.jours_totaux_equipe || 0,
                  heures_totales_equipe: (calculations.jours_totaux_equipe || 0) * 8,
                  jours_effectifs: calculations.jours_effectifs || 0,
                  cout_journalier_equipe: calculations.cout_journalier_equipe || 0,
                }}
              />
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Récapitulatif Devis Actif */}
      {devisActif && (
        <Card className="card-premium">
          <CardContent className="pt-6">
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">💰 Devis actif ({devisActif.version})</p>
                  <p className="text-2xl font-bold">{devisActif.montant_ht?.toLocaleString() || 0} € HT</p>
                </div>
                <Badge className="bg-primary text-primary-foreground">ACTIF</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Métriques temps réel - En dessous du récapitulatif devis */}
      {!metricsLoading && metrics && (
        <>
          <ChantierKpis metrics={metrics} onTabChange={setActiveTab} />
          <div ref={chartsSectionRef}>
            <ChartsSection
              chantierId={id!}
              defaultTab={chartsSectionDefaultTab || (autoOpenBudgetModal ? 'budget' : undefined)}
              autoOpenVentilationModal={autoOpenBudgetModal}
              onVentilationModalOpened={() => setAutoOpenBudgetModal(false)}
              highlightBudgetButton={highlightBudgetButton}
              onBudgetButtonClicked={() => setHighlightBudgetButton(false)}
            />
          </div>
        </>
      )}

      {metricsLoading && (
        <Card className="card-premium">
          <CardContent className="pt-16 pb-16 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent mx-auto mb-4"></div>
            <p className="text-muted-foreground">Chargement des métriques...</p>
          </CardContent>
        </Card>
      )}

      {/* Dialog de clôture */}
      <ChantierClosureDialog
        open={closureDialogOpen}
        onOpenChange={setClosureDialogOpen}
        chantierId={id!}
        chantierData={stableChantierData}
        metrics={stableMetrics}
        membres={membres}
        affectations={affectations}
        devis={devis}
        factures={factures}
        frais={frais}
        onClosed={handleDataUpdate}
      />

      {/* Dialog centralisé de modification */}
      <ChantierFormDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        project={chantier}
        onSuccess={handleDataUpdate}
        entrepriseId={entrepriseId}
        showBudgetField={true}
      />

      {/* Dialog de suppression */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Êtes-vous absolument sûr ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le chantier "{chantier?.nom_chantier}" 
              et toutes ses données associées (devis, factures, équipe, frais) 
              seront définitivement supprimées.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeletingChantier}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteChantier} disabled={isDeletingChantier} className="bg-destructive hover:bg-destructive/90">
              {isDeletingChantier ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Suppression...</>
              ) : (
                "Supprimer définitivement"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* TipsModal - Conseils pour l'ajout de devis */}
      <TipsModal isOpen={showDevisTips} onClose={handleCloseDevisTips} title="Ajouter vos devis">
        <div className="space-y-4">
          <p className="font-medium">Pour ajouter vos devis efficacement :</p>
          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">1.</span>
              <span>Cliquez sur "Nouveau devis"</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">2.</span>
              <span>Sélectionnez le statut du devis (Brouillon, Envoyé, Accepté...)</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">3.</span>
              <span>Importez votre document — notre OCR extrait les montants automatiquement</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">4.</span>
              <span>Vérifiez les informations extraites et ajustez si nécessaire</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">5.</span>
              <span>Validez pour finaliser le devis</span>
            </li>
          </ol>
        </div>
      </TipsModal>

      {/* TipsModal - Conseils pour l'ajout de factures fournisseurs */}
      <TipsModal isOpen={showFacturesTips} onClose={handleCloseFacturesTips} title="Ajouter vos factures fournisseurs">
        <div className="space-y-4">
          <p className="font-medium">Les factures fournisseurs permettent de suivre vos dépenses réelles :</p>
          <ol className="space-y-3">
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">1.</span>
              <span>Cliquez sur <strong>"Ajouter une facture"</strong> dans l'onglet Factures</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">2.</span>
              <span>Renseignez le fournisseur, le montant HT et la catégorie (matériaux, location, etc.)</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">3.</span>
              <span>Vous pouvez aussi utiliser le <strong>Scan IA</strong> pour importer automatiquement depuis une photo ou un PDF</span>
            </li>
            <li className="flex gap-3">
              <span className="font-bold text-white shrink-0">4.</span>
              <span>Chaque facture ajoutée met à jour la rentabilité du chantier en temps réel</span>
            </li>
          </ol>
          <p className="text-sm text-muted-foreground mt-2">
            💡 Astuce : Scannez vos factures en lot avec le Multi-Scan pour gagner du temps.
          </p>
        </div>
      </TipsModal>

      {/* TipsModal - Conseils pour le suivi des paiements */}
      <TipsModal isOpen={showPaiementTips} onClose={handleClosePaiementTips} title="Suivi des paiements">
        <div className="space-y-4">
          <p className="font-medium">
            Comment ajouter un paiement lié à une facture client :
          </p>
          <ol className="space-y-3 list-decimal list-inside">
            <li className="flex gap-3">
              <span className="text-white shrink-0">1.</span>
              <span><strong>Créez d'abord une facture client</strong> — Dans l'onglet "Factures clients", cliquez sur "Créer une facture" et renseignez le numéro, montant et échéance</span>
            </li>
            <li className="flex gap-3">
              <span className="text-white shrink-0">2.</span>
              <span><strong>Ajoutez un paiement</strong> — Dans l'onglet "Paiements", cliquez sur "+ Ajouter" et saisissez le montant reçu</span>
            </li>
            <li className="flex gap-3">
              <span className="text-white shrink-0">3.</span>
              <span><strong>Liez le paiement à la facture</strong> — Sélectionnez la facture dans le champ "Facture liée" pour associer le paiement à votre facture</span>
            </li>
          </ol>
          <div className="bg-muted/50 rounded-lg p-3 mt-4">
            <p className="text-sm font-medium mb-2">Pourquoi lier les paiements aux factures ?</p>
            <ul className="space-y-1 text-sm opacity-70">
              <li>• Suivez facilement ce qui a été payé pour chaque facture</li>
              <li>• Identifiez les factures partiellement réglées</li>
              <li>• Automatisez le suivi de votre trésorerie</li>
            </ul>
          </div>
          <p className="text-sm mt-2 opacity-70">
            💡 Astuce : Vous pouvez aussi ajouter des paiements sans facture pour enregistrer des acomptes ou des règlements directs.
          </p>
        </div>
      </TipsModal>

      {/* TipsModal - Conseils pour la ventilation du budget */}
      <TipsModal isOpen={showBudgetTips} onClose={handleCloseBudgetTips} title="Budget par poste de dépense">
        <div className="space-y-4">
          <p className="font-medium">
            Définissez un budget maximum pour chaque catégorie de dépense :
          </p>
          <ul className="space-y-3">
            <li className="flex gap-3">
              <span className="text-lg shrink-0">👷</span>
              <span><strong>Main d'œuvre</strong> — Salaires et charges de vos équipes</span>
            </li>
            <li className="flex gap-3">
              <span className="text-lg shrink-0">📦</span>
              <span><strong>Matériaux</strong> — Fournitures et approvisionnements</span>
            </li>
            <li className="flex gap-3">
              <span className="text-lg shrink-0">🏗️</span>
              <span><strong>Sous-traitance</strong> — Travaux confiés à des tiers</span>
            </li>
            <li className="flex gap-3">
              <span className="text-lg shrink-0">📋</span>
              <span><strong>Frais annexes</strong> — Location, déplacements, etc.</span>
            </li>
          </ul>
          <div className="mt-4 p-3 bg-primary/10 rounded-lg">
            <p className="text-sm opacity-70">
              💡 <strong>Astuce</strong> : Vous recevrez une alerte si un poste dépasse 90% de son budget,
              même si le budget global est respecté. Cela vous permet d'anticiper les dépassements.
            </p>
          </div>
        </div>
      </TipsModal>

      {/* Modal de suspension */}
      <ModalSuspensionChantier
        isOpen={suspensionModalOpen}
        onClose={() => setSuspensionModalOpen(false)}
        chantierId={id!}
        chantierNom={chantier?.nom_chantier || ''}
        onConfirm={() => {
          setSuspensionModalOpen(false);
          handleDataUpdate();
        }}
      />

      {/* Modal de reprise */}
      {suspensionInfo && (
        <ModalRepriseChantier
          isOpen={repriseModalOpen}
          onClose={() => setRepriseModalOpen(false)}
          chantierId={id!}
          chantierNom={chantier?.nom_chantier || ''}
          suspensionInfo={suspensionInfo}
          onConfirm={() => {
            setRepriseModalOpen(false);
            handleDataUpdate();
          }}
        />
      )}

      {/* Modal d'annulation */}
      <ModalAnnulationChantier
        isOpen={annulationModalOpen}
        onClose={() => setAnnulationModalOpen(false)}
        chantierId={id!}
        chantierNom={chantier?.nom_chantier || ''}
        onConfirm={() => {
          setAnnulationModalOpen(false);
          handleDataUpdate();
        }}
      />
    </div>
  );
};

export default ProjectDetail;
